<template>
  <v-dialog v-model="dialog" persistent max-width="520">
    <template v-slot:activator="{ on, attrs }">
      <v-btn text small color="grey darken-1" v-bind="attrs" v-on="on" class="mt-2">
        <v-icon left small>mdi-lock-reset</v-icon>ลืมรหัสผ่าน?
      </v-btn>
    </template>

    <v-card class="reset-card" rounded="lg">
      <!-- Header -->
      <div class="reset-header">
        <div class="header-icon-wrap">
          <v-icon size="36" color="white">mdi-lock-reset</v-icon>
        </div>
        <div class="ml-4">
          <h3 class="white--text mb-1">รีเซ็ตรหัสผ่าน</h3>
          <p class="white--text caption mb-0" style="opacity:.85">ขั้นตอนที่ {{ step }} จาก 3</p>
        </div>
        <v-spacer />
        <v-btn icon dark @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <!-- Step indicator -->
      <div class="step-bar">
        <div v-for="s in 3" :key="s" class="step-dot" :class="{ active: step >= s, done: step > s }">
          <v-icon v-if="step > s" x-small color="white">mdi-check</v-icon>
          <span v-else class="step-num">{{ s }}</span>
        </div>
        <div class="step-line" :style="{ width: ((step-1)/2*100) + '%' }"></div>
      </div>

      <v-card-text class="px-7 pt-5 pb-4">

        <!-- ======== STEP 1: กรอกข้อมูลตัวตน ======== -->
        <v-form ref="form1" v-if="step === 1" lazy-validation>
          <div class="step-title mb-5">
            <v-icon color="error" class="mr-2">mdi-account-search</v-icon>
            <span>ยืนยันตัวตนและอีเมล</span>
          </div>

          <v-text-field
            v-model="form.identifier"
            :label="config.identifierLabel"
            prepend-icon="mdi-identifier"
            :rules="[v => !!v || 'กรุณากรอกข้อมูล']"
            outlined rounded dense color="error"
            class="mb-3"
          />

          <v-row dense>
            <v-col cols="6">
              <v-text-field
                v-model="form.firstname"
                label="ชื่อ"
                prepend-icon="mdi-account"
                :rules="[v => !!v || 'กรุณากรอกชื่อ']"
                outlined rounded dense color="error"
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="form.lastname"
                label="นามสกุล"
                :rules="[v => !!v || 'กรุณากรอกนามสกุล']"
                outlined rounded dense color="error"
              />
            </v-col>
          </v-row>

          <v-text-field
            v-if="config.requireTel"
            v-model="form.tel"
            label="เบอร์โทรติดต่อ"
            prepend-icon="mdi-phone"
            :rules="[v => !!v || 'กรุณากรอกเบอร์โทร', v => /^[0-9]{9,10}$/.test(v) || 'รูปแบบเบอร์โทรไม่ถูกต้อง']"
            outlined rounded dense color="error"
            class="mb-3"
          />

          <v-text-field
            v-model="form.email"
            label="อีเมลที่ลงทะเบียนไว้ในระบบ"
            prepend-icon="mdi-email"
            :rules="[v => !!v || 'กรุณากรอกอีเมล', v => /.+@.+\..+/.test(v) || 'รูปแบบอีเมลไม่ถูกต้อง']"
            outlined rounded dense color="error"
          />
        </v-form>

        <!-- ======== STEP 2: ยืนยัน OTP ======== -->
        <div v-if="step === 2">
          <div class="step-title mb-4">
            <v-icon color="error" class="mr-2">mdi-shield-key</v-icon>
            <span>ยืนยันรหัส OTP</span>
          </div>
          <div class="otp-notice mb-5">
            <v-icon color="success" small class="mr-2">mdi-email-check</v-icon>
            <span class="caption">ส่งรหัส OTP 6 หลักไปยัง <strong>{{ maskedEmail }}</strong> แล้ว<br>
            <span class="grey--text">รหัสมีอายุ 15 นาที</span></span>
          </div>

          <v-form ref="form2" lazy-validation>
            <div class="otp-input-wrap mb-4">
              <v-otp-input
                v-if="useOtpInput"
                v-model="form.otp"
                length="6"
                type="number"
                @finish="onOtpFinish"
              />
              <v-text-field
                v-else
                v-model="form.otp"
                label="รหัส OTP 6 หลัก"
                prepend-icon="mdi-numeric"
                :rules="[v => !!v || 'กรุณากรอก OTP', v => /^\d{6}$/.test(v) || 'OTP ต้องเป็นตัวเลข 6 หลัก']"
                outlined rounded dense color="error"
                maxlength="6" counter
              />
            </div>
          </v-form>

          <div class="text-center">
            <v-btn text small color="grey" :disabled="resendCountdown > 0" @click="resendOtp">
              <v-icon left x-small>mdi-refresh</v-icon>
              ส่ง OTP ใหม่ {{ resendCountdown > 0 ? `(${resendCountdown}s)` : '' }}
            </v-btn>
          </div>
        </div>

        <!-- ======== STEP 3: ตั้งรหัสผ่านใหม่ ======== -->
        <v-form ref="form3" v-if="step === 3" lazy-validation>
          <div class="step-title mb-5">
            <v-icon color="error" class="mr-2">mdi-lock-plus</v-icon>
            <span>ตั้งรหัสผ่านใหม่</span>
          </div>

          <v-text-field
            v-model="form.new_password"
            label="รหัสผ่านใหม่"
            prepend-icon="mdi-lock-outline"
            :append-icon="showPwd1 ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showPwd1 ? 'text' : 'password'"
            @click:append="showPwd1 = !showPwd1"
            :rules="pwdRules"
            outlined rounded dense color="error"
            class="mb-1"
            hint="ตัวอักษร+ตัวเลข อย่างน้อย 8 ตัว"
            persistent-hint
          />

          <!-- Password strength bar -->
          <div class="pwd-strength mb-4 mt-2">
            <div
              v-for="i in 4" :key="i"
              class="pwd-bar"
              :class="pwdStrengthClass(i)"
            ></div>
            <span class="caption ml-2" :class="pwdStrengthColor">{{ pwdStrengthLabel }}</span>
          </div>

          <v-text-field
            v-model="form.confirm_password"
            label="ยืนยันรหัสผ่านใหม่"
            prepend-icon="mdi-lock-check"
            :append-icon="showPwd2 ? 'mdi-eye' : 'mdi-eye-off'"
            :type="showPwd2 ? 'text' : 'password'"
            @click:append="showPwd2 = !showPwd2"
            :rules="[v => !!v || 'กรุณายืนยันรหัสผ่าน', v => v === form.new_password || 'รหัสผ่านไม่ตรงกัน']"
            outlined rounded dense color="error"
          />
        </v-form>

        <!-- Success Message -->
        <div v-if="step === 4" class="text-center py-6">
          <v-icon color="success" size="72" class="mb-4">mdi-check-circle</v-icon>
          <h3 class="mb-2">เปลี่ยนรหัสผ่านสำเร็จ!</h3>
          <p class="grey--text">กรุณาเข้าสู่ระบบด้วยรหัสผ่านใหม่</p>
        </div>

        <!-- Error Alert -->
        <v-alert v-if="errorMsg" type="error" dense outlined class="mt-3 mb-0">
          {{ errorMsg }}
        </v-alert>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-5">
        <v-btn
          v-if="step > 1 && step < 4"
          text rounded outlined color="grey"
          @click="step--" :disabled="loading"
        >
          <v-icon left>mdi-arrow-left</v-icon>ย้อนกลับ
        </v-btn>
        <v-spacer />
        <v-btn
          v-if="step === 4"
          rounded color="success" @click="closeDialog"
        >
          <v-icon left>mdi-login-variant</v-icon>เข้าสู่ระบบ
        </v-btn>
        <v-btn
          v-else
          rounded class="action-btn" elevation="0"
          @click="handleNext" :loading="loading"
        >
          {{ step === 3 ? 'บันทึกรหัสผ่านใหม่' : 'ถัดไป' }}
          <v-icon right v-if="step < 3">mdi-arrow-right</v-icon>
          <v-icon right v-else>mdi-content-save</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/**
 * ResetPasswordDialog.vue — 3-step OTP reset (ยืนยันตัวตน → OTP → ตั้งรหัสผ่านใหม่)
 * ใช้ร่วมกันทั้ง 4 ฟอร์ม login ผ่าน prop personType กำหนด endpoint/ฟิลด์ที่ backend คาดหวัง
 */
const PERSON_TYPE_CONFIG = {
  college: {
    endpoint: 'user.reset_password.php',
    identifierLabel: 'รหัสผู้ใช้งาน (รหัสสถานศึกษา / บัตรประชาชน)',
    payloadKeys: { identifier: 'user_name', firstname: 'user_firstname', lastname: 'user_lastname', tel: 'user_tel_p', email: 'user_email' },
    requireTel: true
  },
  // ครู/พนักงานราชการ ใช้ตาราง personnel_temporary ร่วมกัน (แยกกันด้วย user_status ตอน login เท่านั้น)
  personnel: {
    endpoint: 'personnel_temporary.reset_password.php',
    identifierLabel: 'เลขบัตรประชาชน',
    payloadKeys: { identifier: 'id_card', firstname: 'frist_name', lastname: 'last_name', email: 'e_mail' },
    requireTel: false
  },
  personnel_etc: {
    endpoint: 'personnel_etc.reset_password.php',
    identifierLabel: 'เลขบัตรประชาชน',
    payloadKeys: {
      identifier: 'personnel_etc_id_card',
      firstname: 'personnel_etc_fristname',
      lastname: 'personnel_etc_lastname',
      email: 'personnel_etc_email'
    },
    requireTel: false
  }
};

export default {
  name: 'ResetPasswordDialog',
  props: {
    personType: {
      type: String,
      default: 'college',
      validator: v => Object.keys(PERSON_TYPE_CONFIG).includes(v)
    }
  },
  data() {
    return {
      ApiKey: 'HRvec2021',
      dialog: false,
      step: 1,
      loading: false,
      errorMsg: '',
      resetToken: '',
      showPwd1: false,
      showPwd2: false,
      resendCountdown: 0,
      useOtpInput: true,
      form: {
        identifier: '',
        firstname: '',
        lastname: '',
        tel: '',
        email: '',
        otp: '',
        new_password: '',
        confirm_password: '',
      },
      pwdRules: [
        v => !!v || 'กรุณากรอกรหัสผ่านใหม่',
        v => v.length >= 8 || 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร',
        v => /[a-zA-Z]/.test(v) || 'ต้องมีตัวอักษรภาษาอังกฤษ',
        v => /[0-9]/.test(v) || 'ต้องมีตัวเลขอย่างน้อย 1 ตัว',
      ],
    };
  },
  computed: {
    config() {
      return PERSON_TYPE_CONFIG[this.personType];
    },
    maskedEmail() {
      const e = this.form.email;
      if (!e) return '';
      const [local, domain] = e.split('@');
      const masked = local.substring(0, 2) + '***' + local.slice(-1);
      return `${masked}@${domain}`;
    },
    pwdStrength() {
      const p = this.form.new_password;
      if (!p) return 0;
      let s = 0;
      if (p.length >= 8) s++;
      if (/[A-Z]/.test(p)) s++;
      if (/[0-9]/.test(p)) s++;
      if (/[^a-zA-Z0-9]/.test(p)) s++;
      return s;
    },
    pwdStrengthLabel() {
      return ['', 'อ่อน', 'พอใช้', 'ดี', 'แข็งแรง'][this.pwdStrength] || '';
    },
    pwdStrengthColor() {
      return ['', 'red--text', 'orange--text', 'blue--text', 'green--text'][this.pwdStrength] || '';
    },
  },
  methods: {
    pwdStrengthClass(i) {
      if (this.pwdStrength < i) return 'bar-empty';
      return ['', 'bar-weak', 'bar-fair', 'bar-good', 'bar-strong'][this.pwdStrength];
    },
    closeDialog() {
      this.dialog = false;
      setTimeout(() => { this.resetForm(); }, 300);
    },
    resetForm() {
      this.step = 1;
      this.errorMsg = '';
      this.resetToken = '';
      this.form = { identifier: '', firstname: '', lastname: '',
                    tel: '', email: '', otp: '', new_password: '', confirm_password: '' };
      this.$refs.form1?.reset();
    },
    onOtpFinish() { this.handleNext(); },

    async handleNext() {
      this.errorMsg = '';
      if (this.step === 1) {
        if (!this.$refs.form1.validate()) return;
        await this.submitVerify();
      } else if (this.step === 2) {
        if (!/^\d{6}$/.test(this.form.otp)) {
          this.errorMsg = 'กรุณากรอก OTP 6 หลัก';
          return;
        }
        await this.submitConfirm();
      } else if (this.step === 3) {
        if (!this.$refs.form3.validate()) return;
        await this.submitChangePassword();
      }
    },

    async submitVerify() {
      this.loading = true;
      try {
        const keys = this.config.payloadKeys;
        const payload = { ApiKey: this.ApiKey };
        payload[keys.identifier] = this.form.identifier;
        payload[keys.firstname] = this.form.firstname;
        payload[keys.lastname] = this.form.lastname;
        payload[keys.email] = this.form.email;
        if (keys.tel) payload[keys.tel] = this.form.tel;

        const res = await this.$http.post(`${this.config.endpoint}?crud=verify`, payload);
        if (res.data.status) {
          this.resetToken = res.data.token;
          this.step = 2;
          this.startResendCountdown();
        } else {
          this.errorMsg = res.data.msg || 'ไม่สามารถยืนยันตัวตนได้';
        }
      } catch {
        this.errorMsg = 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
      } finally {
        this.loading = false;
      }
    },

    // OTP confirm ขั้นนี้แค่ตรวจสอบว่า OTP ถูก ก่อนไปตั้งรหัสผ่าน
    async submitConfirm() {
      this.loading = true;
      try {
        const res = await this.$http.post(`${this.config.endpoint}?crud=verify_otp`, {
          ApiKey: this.ApiKey,
          token: this.resetToken,
          otp:   this.form.otp,
        });
        if (res.data.status) {
          this.step = 3;
        } else {
          this.errorMsg = res.data.msg || 'รหัส OTP ไม่ถูกต้อง';
        }
      } catch {
        this.errorMsg = 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
      } finally {
        this.loading = false;
      }
    },

    async submitChangePassword() {
      this.loading = true;
      try {
        const res = await this.$http.post(`${this.config.endpoint}?crud=confirm`, {
          ApiKey:       this.ApiKey,
          token:        this.resetToken,
          otp:          this.form.otp,
          new_password: this.form.new_password,
        });
        if (res.data.status) {
          this.step = 4;
        } else {
          this.errorMsg = res.data.msg || 'ไม่สามารถเปลี่ยนรหัสผ่านได้';
        }
      } catch {
        this.errorMsg = 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
      } finally {
        this.loading = false;
      }
    },

    async resendOtp() {
      this.form.otp = '';
      await this.submitVerify();
    },

    startResendCountdown() {
      this.resendCountdown = 60;
      const t = setInterval(() => {
        this.resendCountdown--;
        if (this.resendCountdown <= 0) clearInterval(t);
      }, 1000);
    },
  },
};
</script>

<style scoped>
.reset-header {
  background: #c53030;
  padding: 20px 24px;
  display: flex;
  align-items: center;
}

.header-icon-wrap {
  width: 56px; height: 56px;
  background: rgba(255,255,255,0.2);
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

/* Step bar */
.step-bar {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 48px;
  background: #fff5f5;
  border-bottom: 1px solid #fed7d7;
}

.step-line {
  position: absolute;
  top: 50%;
  left: 48px;
  height: 3px;
  background: #e53e3e;
  transition: width 0.4s ease;
  z-index: 0;
}

.step-dot {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: #e2e8f0;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 600; color: #718096;
  z-index: 1; position: relative;
  transition: all 0.3s ease;
  border: 2px solid #e2e8f0;
}

.step-dot.active {
  background: #e53e3e;
  color: white;
  border-color: #e53e3e;
  box-shadow: 0 0 0 4px rgba(229,62,62,0.15);
}

.step-dot.done {
  background: #38a169;
  border-color: #38a169;
}

.step-num { font-size: 13px; }

.step-title {
  font-size: 15px;
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
}

/* OTP notice */
.otp-notice {
  background: #f0fff4;
  border: 1px solid #9ae6b4;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: flex-start;
}

/* Password strength */
.pwd-strength {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pwd-bar {
  height: 5px;
  flex: 1;
  border-radius: 3px;
  background: #e2e8f0;
  transition: background 0.3s;
}
.bar-weak   { background: #fc8181; }
.bar-fair   { background: #f6ad55; }
.bar-good   { background: #63b3ed; }
.bar-strong { background: #48bb78; }
.bar-empty  { background: #e2e8f0; }

/* Action button */
.action-btn {
  background: #c53030 !important;
  color: white !important;
  font-weight: 600;
  text-transform: none;
  min-width: 140px;
}
</style>
